<template>
  <section :class="$style.contactCta" data-test="gallery-contact-cta">
    <div :class="$style.content">
      <div
        v-if="variant === GalleryContactCtaVariant.DETAIL && advertiserLogoUrl"
        :class="$style.advertiser"
      >
        <img :src="advertiserLogoUrl" :alt="advertiserName || ''" :class="$style.logo" />
      </div>

      <h2>{{ t('imageGallery.contact.title') }}</h2>
      <p>
        {{
          advertiserName
            ? t('imageGallery.contact.withAdvertiser.description', { displayName: advertiserName })
            : t('imageGallery.contact.description')
        }}
      </p>

      <HgButton
        theme="primary"
        icon="Email"
        :class="$style.emailButton"
        data-test="gallery-contact-email"
        @click="emit('emailClick')"
      >
        {{ t('imageGallery.contact.emailButton') }}
      </HgButton>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { HgButton } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { GalleryContactCtaVariant } from '@/models';
import { useListerBrandingStore } from '@/store/modules/lister-branding';

defineOptions({ name: 'GalleryContactCta' });

withDefaults(
  defineProps<{
    variant?: GalleryContactCtaVariant;
  }>(),
  {
    variant: GalleryContactCtaVariant.COVER,
  },
);

const emit = defineEmits<{
  emailClick: [];
}>();

const { t } = useI18n();
const listerBrandingStore = useListerBrandingStore();
const advertiserName = computed(
  () => listerBrandingStore.listerBranding?.legalName || listerBrandingStore.listerBranding?.name,
);
const advertiserLogoUrl = computed(() => listerBrandingStore.listerBranding?.logoUrl);
</script>

<style lang="scss" module>
@import '@/assets/variables';

.contactCta {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: $space-300;
  color: $color-grey-120;
  background-color: $color-white;

  @media (max-width: $viewport-md-min) {
    padding-inline: 4.5rem; // follow design
  }
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 343px;
  text-align: center;

  h2 {
    margin: 0 0 $space-50;
    @include body-2-bold;
  }

  p {
    margin: 0 0 $space-150;
    @include body-2;
  }
}

.emailButton {
  width: 100%;
}

.advertiser {
  margin-bottom: $space-150;
}

.logo {
  display: block;
  width: 140px;
  height: 56px;
  object-fit: contain;
  border-radius: $border-radius;
}
</style>
